import { createContext, useCallback, useContext, useState } from 'react';
import { Layout } from '../../../queries/ats/layout/types';

interface PageContextValues extends Layout.PageView {
  promotionSeen: (id: string) => void;
  hasSeenPromotion: (id: string) => boolean;
}

export const PageContext = createContext<PageContextValues | undefined>(undefined);

export function usePageContext(name: string) {
  const context = useContext(PageContext);

  if (!context) {
    throw new Error(`${name} tried to usePageContext outside of PageContextProvider`);
  }

  return context;
}

interface PageContextProviderProps {
  page: Layout.PageView;
  children: React.ReactNode;
}

export function PageContextProvider({ page, children }: PageContextProviderProps) {
  const [seenPromotions, setSeenPromotions] = useState<string[]>([]);

  const promotionSeen = useCallback((id: string) => {
    setSeenPromotions((prev) => [...prev, id]);
  }, []);

  const hasSeenPromotion = useCallback(
    (id: string) => {
      const promotion = seenPromotions.find((promotionId) => promotionId === id);
      return promotion !== undefined;
    },
    [seenPromotions],
  );

  return (
    <PageContext.Provider
      value={{
        ...page,
        promotionSeen,
        hasSeenPromotion,
      }}
    >
      {children}
    </PageContext.Provider>
  );
}
